/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  ArticleRenderer.tsx — ForexFundAI Dynamic Article Engine v2.0         ║
 * ║                                                                          ║
 * ║  Designed to work as a direct consumer of Agent 6 (UX Formatter)       ║
 * ║  output contract. Handles two rendering modes:                          ║
 * ║                                                                          ║
 * ║  MODE A — Legacy Markdown (content only, no manifest):                  ║
 * ║    Renders plain Markdown/HTML with all existing rich formatting.        ║
 * ║                                                                          ║
 * ║  MODE B — Dynamic Manifest Mode (content + manifest):                   ║
 * ║    Parses [RENDER_COMP_X] placeholders, looks them up in manifest,      ║
 * ║    and renders the correct UI component from the library.               ║
 * ║                                                                          ║
 * ║  COMPONENT LIBRARY (10 stubs, ready for production upgrade):            ║
 * ║    StatPulse, CalloutBox, MermaidChart, PriceActionTimeline,            ║
 * ║    TradingSetupCard, ComparisonMatrix, COTAnalysisTable,                ║
 * ║    SentimentHeatmap, LiquidityMap, RiskCalculator                       ║
 * ║                                                                          ║
 * ║  SAFETY NET: ComponentErrorBoundary wraps every injected component.     ║
 * ║  A failed component silently degrades — the article text always shows.  ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */

import React, {
  Component,
  Suspense,
  lazy,
  useState,
  useMemo,
  type ReactNode,
  type ErrorInfo,
} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Link } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Minus, Info, AlertTriangle,
  CheckCircle, Zap, ShieldAlert, BarChart3, Target, Crosshair,
  Landmark, Activity, AlertCircle, ChevronRight, TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon, Minus as MinusIcon
} from 'lucide-react';

// Lazy-load Mermaid — large bundle, never block initial paint
const MermaidChart = lazy(() => import('./MermaidChart'));

// ═══════════════════════════════════════════════════════════════════════════
// TYPES  — mirrors Agent 6's output contract exactly
// ═══════════════════════════════════════════════════════════════════════════

export type Personality = 'bullish' | 'bearish' | 'neutral' | 'warning' | 'institutional' | 'info' | string;

export interface ManifestEntry {
  component_id: string;       // e.g. "TradingSetupCard"
  personality: Personality;   // e.g. "bullish"
  data: Record<string, any>;  // dynamic payload from Agent 6
}

export interface ArticleManifest {
  [placeholder: string]: ManifestEntry; // key = "RENDER_COMP_0" etc.
}

export interface ArticleRendererProps {
  content: string;
  manifest?: ArticleManifest | null;
  className?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS — synced with Agent 6's DesignSystem class
// ═══════════════════════════════════════════════════════════════════════════

const DS = {
  accent: '#D4A537',
  accentHover: '#EAB308',
  bgDark: '#0F1419',
  bgCard: '#1A1F2E',
  bullish: '#10B981',
  bullishBg: 'rgba(16,185,129,0.12)',
  bearish: '#EF4444',
  bearishBg: 'rgba(239,68,68,0.12)',
  neutral: '#3B82F6',
  neutralBg: 'rgba(59,130,246,0.12)',
  institutional: '#8B5CF6',
  institutionalBg: 'rgba(139,92,246,0.12)',
  warning: '#F59E0B',
  warningBg: 'rgba(245,158,11,0.12)',
} as const;

/** Resolve personality → color tokens */
function personalityTokens(personality: Personality) {
  switch (personality) {
    case 'bullish': return { border: DS.bullish, bg: DS.bullishBg, text: DS.bullish, badge: 'BULLISH', badgeBg: DS.bullishBg };
    case 'bearish': return { border: DS.bearish, bg: DS.bearishBg, text: DS.bearish, badge: 'BEARISH', badgeBg: DS.bearishBg };
    case 'institutional': return { border: DS.institutional, bg: DS.institutionalBg, text: DS.institutional, badge: 'INSTITUTIONAL', badgeBg: DS.institutionalBg };
    case 'warning': return { border: DS.warning, bg: DS.warningBg, text: DS.warning, badge: 'WARNING', badgeBg: DS.warningBg };
    case 'info': return { border: DS.neutral, bg: DS.neutralBg, text: DS.neutral, badge: 'INFO', badgeBg: DS.neutralBg };
    default: return { border: DS.neutral, bg: DS.neutralBg, text: DS.neutral, badge: personality?.toUpperCase() || 'NEUTRAL', badgeBg: DS.neutralBg };
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// ERROR BOUNDARY — wraps every injected component individually
// ═══════════════════════════════════════════════════════════════════════════

interface BoundaryProps { children: ReactNode; componentId: string; }
interface BoundaryState { hasError: boolean; error: Error | null; }

class ComponentErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  constructor(props: BoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Non-blocking — just log
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[ArticleRenderer] Component "${this.props.componentId}" failed to render:`, error, info);
    }
  }

  render() {
    if (this.state.hasError) {
      // Fail gracefully — render nothing, article text continues
      return process.env.NODE_ENV !== 'production' ? (
        <div
          className="my-4 p-3 text-xs font-mono text-rose-400 bg-rose-950/30 border border-rose-800/50 rounded-lg"
          title={this.state.error?.message}
        >
          ⚠ [{this.props.componentId}] render error — component hidden in production
        </div>
      ) : null;
    }
    return this.props.children;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// SHARED STUB SHELL — every stub uses this frame for visual consistency
// ═══════════════════════════════════════════════════════════════════════════

function StubShell({
  componentId,
  personality,
  icon,
  children,
}: {
  componentId: string;
  personality: Personality;
  icon: string;
  children: ReactNode;
}) {
  const tok = personalityTokens(personality);
  return (
    <div
      className="my-8 rounded-2xl overflow-hidden shadow-xl"
      style={{
        background: `linear-gradient(135deg, ${DS.bgCard} 0%, ${tok.bg} 100%)`,
        border: `1px solid ${tok.border}40`,
      }}
    >
      {/* Header bar */}
      <div
        className="flex items-center gap-3 px-5 py-3 border-b"
        style={{ borderColor: `${tok.border}30`, background: `${tok.bg}` }}
      >
        <span className="text-xl">{icon}</span>
        <span className="text-xs font-bold tracking-widest uppercase" style={{ color: tok.text }}>
          {componentId}
        </span>
        <span
          className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
          style={{ background: tok.badgeBg, color: tok.text, border: `1px solid ${tok.border}50` }}
        >
          {tok.badge}
        </span>
      </div>
      {/* Body */}
      <div className="p-5">{children}</div>
    </div>
  );
}

/** Renders a block of JSON data as a formatted dev-mode table */
function DataPreview({ data }: { data: Record<string, any> }) {
  const [expanded, setExpanded] = useState(false);
  const entries = Object.entries(data);
  const visible = expanded ? entries : entries.slice(0, 6);
  return (
    <div className="space-y-2">
      {visible.map(([key, val]) => (
        <div key={key} className="flex gap-3 text-sm">
          <span className="min-w-[140px] text-gray-500 font-mono">{key}</span>
          <span className="text-gray-300 font-mono break-all">
            {typeof val === 'object' ? (
              <pre className="text-xs text-gray-400 whitespace-pre-wrap">{JSON.stringify(val, null, 2)}</pre>
            ) : String(val)}
          </span>
        </div>
      ))}
      {entries.length > 6 && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="text-xs text-gray-500 hover:text-gray-300 transition-colors mt-1"
        >
          {expanded ? '▲ collapse' : `▼ +${entries.length - 6} more fields`}
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ── STUB COMPONENTS (14) ─────────────────────────────────────────────────
// Each accepts { personality, data } and renders a rich design preview.
// ═══════════════════════════════════════════════════════════════════════════

/** 0. MidArticleCTA — Institutional Call to Action */
function MidArticleCTA({ personality, data }: { personality: Personality; data: any }) {
  const title = data.title || "Institutional Execution Protocol";
  const body = data.body || "Unlock professional-grade liquidity heatmaps and COT positioning data.";
  const buttonText = data.buttonText || "Upgrade to Pro Intelligence";
  const href = data.href || "/pricing";

  return (
    <div className="my-12 relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#1A1F2E] to-[#0A0E17] border border-white/5 p-1 group shadow-2xl">
      <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-5 pointer-events-none" />
      <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#D4A537]/10 blur-[100px] rounded-full group-hover:bg-[#D4A537]/20 transition-all duration-700" />
      <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-blue-500/5 blur-[100px] rounded-full" />
      
      <div className="relative z-10 p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="flex-1 text-center md:text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4A537]/10 border border-[#D4A537]/20 mb-6">
            <Zap className="w-3 h-3 text-[#D4A537] fill-[#D4A537]" />
            <span className="text-[10px] font-black text-[#D4A537] uppercase tracking-[3px]">Member Intelligence</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-black text-white mb-4 tracking-tighter uppercase font-['Orbitron'] leading-tight">
            {title}
          </h2>
          <p className="text-gray-400 text-base md:text-lg max-w-xl leading-relaxed font-medium">
            {body}
          </p>
        </div>
        
        <div className="shrink-0">
          <Link to={href} className="group/btn relative px-8 py-4 bg-[#D4A537] hover:bg-[#F2C94C] text-black font-black uppercase tracking-[2px] text-sm rounded-xl transition-all duration-300 shadow-[0_20px_40px_-15px_rgba(212,165,55,0.4)] hover:shadow-[0_25px_50px_-12px_rgba(212,165,55,0.6)] hover:-translate-y-1 inline-flex items-center gap-2 no-underline">
            <span className="text-black font-black uppercase tracking-[2px]">{buttonText}</span>
            <ChevronRight className="w-4 h-4 text-black transition-transform group-hover/btn:translate-x-1" />
          </Link>
        </div>
      </div>
    </div>
  );
}

/** 1. StatPulse — Advanced Animated Statistic */
function StatPulse({ personality, data }: { personality: Personality; data: any }) {
  // Support both single entry and array of stats from Agent 6
  const stats = data?.stats && Array.isArray(data.stats) ? data.stats :
    (data?.value ? [{ value: data.value, label: data.label, trend: data.trend, trend_label: data.trend_label, context: data.context }] : []);

  if (stats.length === 0) return null;

  const tok = personalityTokens(personality);

  return (
    <div className="my-10 space-y-6">
      {stats.map((s: any, idx: number) => {
        if (!s.value || s.value === 'N/A') return null;

        const value = s.value;
        const label = s.label && s.label !== 'N/A' ? s.label : 'Key Data';
        const context = s.context && s.context !== 'N/A' ? s.context : null;
        const trend = s.trend || 'FLAT';
        const trendLabel = s.trend_label && s.trend_label !== 'N/A' ? s.trend_label : null;

        const isUp = trend === 'UP' || trend === 'BULLISH';
        const isDown = trend === 'DOWN' || trend === 'BEARISH';
        const trendColor = isUp ? DS.bullish : isDown ? DS.bearish : DS.neutral;
        const TrendIcon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;

        return (
          <div key={idx} className="relative overflow-hidden group">
            <div
              className="absolute inset-0 opacity-20 blur-3xl rounded-[3rem] transition-opacity duration-1000 group-hover:opacity-40"
              style={{ background: `linear-gradient(to right, transparent, ${tok.border}, transparent)` }}
            />
            <div
              className="relative z-10 flex flex-col items-center justify-center p-8 rounded-3xl border shadow-2xl backdrop-blur-md"
              style={{
                background: `linear-gradient(145deg, ${DS.bgCard}dd 0%, ${tok.bg}dd 100%)`,
                borderColor: `${tok.border}40`,
                boxShadow: `0 8px 32px 0 rgba(0,0,0,0.5), inset 0 1px 0 0 rgba(255,255,255,0.05)`
              }}
            >
              <div
                className="text-[3.5rem] md:text-6xl font-black tracking-tighter leading-none mb-3 bg-clip-text text-transparent"
                style={{
                  backgroundImage: `linear-gradient(to bottom right, #fff 20%, ${tok.text} 100%)`,
                  filter: `drop-shadow(0 0 15px ${tok.border}60)`
                }}
              >
                {value}
              </div>
              <div className="text-base font-bold tracking-widest uppercase text-gray-300 mb-2">{label}</div>

              {trendLabel && (
                <div
                  className="flex items-center gap-2 px-4 py-1.5 rounded-full font-bold text-sm shadow-inner"
                  style={{ backgroundColor: `${trendColor}20`, color: trendColor, border: `1px solid ${trendColor}50` }}
                >
                  <TrendIcon strokeWidth={3} className="w-4 h-4" />
                  <span>{trendLabel}</span>
                </div>
              )}

              {context && <div className="mt-4 text-sm text-gray-400 max-w-md text-center leading-relaxed">{context}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** 2. CalloutBox — Advanced Highlighted Insight */
function CalloutBox({ personality, data }: { personality: Personality; data: any }) {
  if (!data?.content || data.content === 'N/A') return null;

  const variant = data.variant ?? personality;
  const tok = personalityTokens(variant);
  const title = data.title && data.title !== 'N/A' ? data.title : null;

  let Icon = Info;
  if (variant === 'warning') Icon = AlertTriangle;
  else if (variant === 'danger' || variant === 'bearish') Icon = AlertCircle;
  else if (variant === 'success' || variant === 'bullish') Icon = CheckCircle;
  else if (variant === 'institutional') Icon = Landmark;

  return (
    <div
      className="my-8 flex gap-4 p-6 rounded-2xl relative overflow-hidden backdrop-blur-md shadow-lg transition-transform hover:-translate-y-0.5"
      style={{
        background: `linear-gradient(90deg, ${tok.bg}ee 0%, ${DS.bgCard}ee 100%)`,
        border: `1px solid ${tok.border}40`,
        borderLeft: `5px solid ${tok.border}`,
      }}
    >
      <div
        className="absolute top-0 right-0 w-32 h-32 opacity-10 blur-2xl rounded-full translate-x-10 -translate-y-10"
        style={{ background: tok.border }}
      />
      <div
        className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center shadow-inner"
        style={{ background: `${tok.border}25` }}
      >
        <Icon className="w-5 h-5" style={{ color: tok.border }} strokeWidth={2.5} />
      </div>
      <div className="flex-1 relative z-10 pt-1">
        {title && <h4 className="font-bold text-white text-lg mb-1.5 tracking-wide">{title}</h4>}
        <div className="text-[15px] text-gray-300 leading-relaxed font-medium">{data.content}</div>
      </div>
    </div>
  );
}

/** 3. MermaidChart — Advanced Diagram Wrapper */
function MermaidChartStub({ personality, data }: { personality: Personality; data: any }) {
  if (!data?.chart || data.chart === 'N/A') return null;

  const tok = personalityTokens(personality);
  const chartStr = data.chart;
  const title = data.title && data.title !== 'N/A' ? data.title : 'Technical Flow Analysis';
  const caption = data.caption && data.caption !== 'N/A' ? data.caption : null;

  return (
    <div className="my-10 p-1 rounded-3xl" style={{ background: `linear-gradient(135deg, ${tok.border}60 0%, ${tok.border}00 100%)` }}>
      <div className="bg-[#090D14] rounded-[1.4rem] p-6 border shadow-2xl" style={{ borderColor: `${tok.border}20` }}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center border shadow-inner" style={{ background: `${tok.border}15`, borderColor: `${tok.border}30` }}>
            <Zap className="w-5 h-5" style={{ color: tok.border }} />
          </div>
          <h3 className="text-xl font-bold text-white tracking-tight">{title}</h3>
        </div>

        <div className="bg-[#05070A] rounded-2xl p-4 sm:p-6 border border-gray-800/60 overflow-x-auto min-h-[160px] flex items-center justify-center">
          <Suspense
            fallback={
              <div className="flex flex-col items-center justify-center p-8 gap-3">
                <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: `${tok.border} transparent ${tok.border} ${tok.border}` }} />
                <span className="text-xs font-bold uppercase tracking-widest text-gray-500">Rendering Diagram...</span>
              </div>
            }
          >
            <MermaidChart chart={chartStr} caption="" />
          </Suspense>
        </div>

        {caption && (
          <div className="mt-4 text-center text-[10px] sm:text-xs text-gray-500 italic font-medium">
            {caption}
          </div>
        )}
      </div>
    </div>
  );
}

/** 4. PriceActionTimeline — Advanced Institutional Timeline */
function PriceActionTimeline({ personality, data }: { personality: Personality; data: any }) {
  if (!data?.events || !Array.isArray(data.events) || data.events.length === 0) return null;

  const tok = personalityTokens(personality);
  const title = data.title && data.title !== 'N/A' ? data.title : 'Market Timeline';
  const asset = data.asset && data.asset !== 'N/A' ? data.asset : null;

  const getEventMeta = (type: string) => {
    switch (type) {
      case 'HIGH': return { color: DS.bullish, Icon: TrendingUp };
      case 'LOW': return { color: DS.bearish, Icon: TrendingDown };
      case 'BREAKOUT': return { color: DS.accent, Icon: Zap };
      case 'REJECTION': return { color: DS.bearish, Icon: ShieldAlert };
      case 'SWEEP': return { color: DS.institutional, Icon: Target };
      case 'NEWS': return { color: DS.warning, Icon: AlertTriangle };
      default: return { color: DS.neutral, Icon: Activity };
    }
  };

  return (
    <div className="my-10 p-6 sm:p-8 rounded-3xl bg-[#090D14] border shadow-2xl" style={{ borderColor: `${tok.border}30` }}>
      <div className="mb-8 border-b pb-4" style={{ borderColor: `${tok.border}20` }}>
        <h3 className="text-2xl font-black text-white tracking-tight">{title}</h3>
        {asset && <div className="text-sm font-semibold mt-1" style={{ color: tok.text }}>{asset} — Price Events</div>}
      </div>

      <div className="relative">
        <div
          className="absolute left-[19px] top-4 bottom-4 w-[2px] rounded-full opacity-40 shadow-[0_0_10px_currentColor]"
          style={{ background: `linear-gradient(to bottom, ${tok.border}, transparent)`, color: tok.border }}
        />

        <div className="space-y-8 pl-14">
          {data.events.map((ev: any, i: number) => {
            if (!ev.label || ev.label === 'N/A') return null;
            const meta = getEventMeta(ev.type);
            const { Icon } = meta;
            const price = ev.price && ev.price !== 'N/A' ? Number(ev.price).toLocaleString('en-US', { minimumFractionDigits: 2 }) : null;

            return (
              <div key={i} className="relative group">
                <div
                  className="absolute -left-14 top-0.5 w-10 h-10 rounded-full flex items-center justify-center border-2 shadow-lg transition-transform duration-300 group-hover:scale-110"
                  style={{ background: `${meta.color}20`, borderColor: meta.color, boxShadow: `0 0 15px ${meta.color}40` }}
                >
                  <Icon className="w-5 h-5" style={{ color: meta.color }} strokeWidth={2.5} />
                </div>

                <div className="bg-[#121824] border rounded-2xl p-4 shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1" style={{ borderColor: `${meta.color}20` }}>
                  <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-3 mb-2">
                    <span
                      className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full"
                      style={{ background: `${meta.color}15`, color: meta.color }}
                    >
                      {ev.type || 'EVENT'}
                    </span>
                    {ev.date && ev.date !== 'N/A' && <span className="text-xs text-gray-500 font-medium">{ev.date}</span>}
                  </div>

                  <div>
                    <div className="text-base sm:text-lg font-bold text-gray-100 mb-1 leading-snug">{ev.label}</div>
                    {price && (
                      <div className="font-mono text-sm font-semibold tracking-wide" style={{ color: meta.color }}>
                        {price}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** 5. TradingSetupCard — Advanced Executable Setup UI */
function TradingSetupCard({ personality, data }: { personality: Personality; data: any }) {
  if (!data?.entry_price || data.entry_price === 'N/A') return null; // Must have an entry to be a setup

  const direction = (data.direction ?? 'LONG').toUpperCase();
  const isLong = direction === 'LONG';
  const cTheme = isLong ? DS.bullish : DS.bearish;

  const fmt = (v: any) => v && v !== 'N/A' ? Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 }) : null;
  const entry = fmt(data.entry_price);
  const sl = fmt(data.stop_loss);
  const tp1 = fmt(data.take_profit_1);
  const tp2 = fmt(data.take_profit_2);
  const rr = data.risk_reward && data.risk_reward !== 'N/A' ? data.risk_reward : null;
  const timeframe = data.timeframe && data.timeframe !== 'N/A' ? data.timeframe : null;

  const validLevels = [
    { label: 'Entry', val: entry, col: DS.neutral },
    { label: 'Stop Loss', val: sl, col: DS.bearish },
    { label: 'TP 1', val: tp1, col: DS.bullish },
    { label: 'TP 2', val: tp2, col: DS.bullish },
  ].filter(x => x.val !== null);

  return (
    <div className="my-10 rounded-[2rem] p-1 overflow-hidden" style={{ background: `linear-gradient(135deg, ${DS.bgCard}, ${cTheme}30)` }}>
      <div className="bg-[#0A0E17] rounded-[1.8rem] p-6 sm:p-8 relative h-full">
        {/* Glow orb */}
        <div className="absolute -top-10 -right-10 w-40 h-40 blur-3xl opacity-20 rounded-full" style={{ background: cTheme }} />

        <div className="relative z-10 flex flex-wrap justify-between items-center gap-4 border-b pb-5 mb-6" style={{ borderColor: `${cTheme}25` }}>
          <div>
            <div className="flex items-center gap-3">
              <Crosshair className="w-6 h-6" style={{ color: cTheme }} />
              <h3 className="text-2xl font-black text-white tracking-tight">Trade Blueprint</h3>
            </div>
            {data.asset && data.asset !== 'N/A' && <span className="text-gray-400 font-semibold mt-1 block">{data.asset}</span>}
          </div>
          <div className="flex gap-2">
            {timeframe && <span className="px-3 py-1 bg-gray-800 rounded-full text-xs font-bold text-gray-300 border border-gray-700">{timeframe}</span>}
            <span className="px-4 py-1 rounded-full text-xs font-black tracking-widest uppercase border shadow-[0_0_10px_currentColor]" style={{ background: `${cTheme}15`, color: cTheme, borderColor: `${cTheme}50` }}>
              {isLong ? 'LONG ' : 'SHORT '} {isLong ? '▲' : '▼'}
            </span>
          </div>
        </div>

        <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {validLevels.map((lvl) => (
            <div key={lvl.label} className="bg-[#121824] p-4 rounded-2xl border transition-colors hover:bg-[#1A2333]" style={{ borderColor: `${lvl.col}20` }}>
              <div className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mb-1.5">{lvl.label}</div>
              <div className="font-mono text-lg font-bold" style={{ color: lvl.col }}>{lvl.val}</div>
            </div>
          ))}
        </div>

        <div className="relative z-10 flex flex-col md:flex-row gap-6">
          <div className="flex-1 space-y-4">
            {data.rationale && data.rationale !== 'N/A' && (
              <div>
                <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Rationale</div>
                <p className="text-sm text-gray-300 leading-relaxed bg-[#121824] p-4 rounded-xl border border-[#ffffff08]">{data.rationale}</p>
              </div>
            )}
            {data.invalidation && data.invalidation !== 'N/A' && (
              <div className="flex gap-3 bg-red-950/20 p-4 rounded-xl border border-red-900/40">
                <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                <div className="text-sm text-red-300/90 leading-relaxed"><strong className="text-red-400 block mb-0.5 font-bold uppercase text-[10px] tracking-widest">Invalidation</strong> {data.invalidation}</div>
              </div>
            )}
          </div>

          {rr && (
            <div className="shrink-0 w-full md:w-32 flex flex-col items-center justify-center bg-[#121824] border border-[#ffffff08] rounded-2xl p-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-2 shadow-[0_0_20px_rgba(212,165,55,0.2)]" style={{ background: `${DS.accent}15`, border: `2px solid ${DS.accent}40` }}>
                <Target className="w-6 h-6" style={{ color: DS.accent }} strokeWidth={2.5} />
              </div>
              <div className="text-xs text-gray-500 font-bold uppercase tracking-widest mb-1">R:R</div>
              <div className="text-xl font-black font-mono tracking-tight" style={{ color: DS.accent }}>{rr}</div>
            </div>
          )}
        </div>

        {data.disclaimer && data.disclaimer !== 'N/A' && (
          <div className="relative z-10 mt-6 pt-4 border-t border-gray-800 text-center text-[10px] text-gray-600 font-medium">
            {data.disclaimer}
          </div>
        )}
      </div>
    </div>
  );
}

/** 6. ComparisonMatrix — Advanced Institutional Grid */
function ComparisonMatrix({ personality, data }: { personality: Personality; data: any }) {
  if (!data?.rows || !Array.isArray(data.rows) || data.rows.length === 0) return null;

  const leftHeader = data.left_header && data.left_header !== 'N/A' ? data.left_header : 'Institutional';
  const rightHeader = data.right_header && data.right_header !== 'N/A' ? data.right_header : 'Retail';
  const leftAccent = data.left_accent && data.left_accent !== 'N/A' ? data.left_accent : DS.bullish;
  const rightAccent = data.right_accent && data.right_accent !== 'N/A' ? data.right_accent : DS.bearish;
  const title = data.title && data.title !== 'N/A' ? data.title : 'Comparison Matrix';
  const caption = data.caption && data.caption !== 'N/A' ? data.caption : null;

  return (
    <div className="my-10 bg-[#0A0E17] border border-gray-800 rounded-3xl overflow-hidden shadow-2xl">
      <div className="p-6 sm:p-8 pt-6 border-b border-gray-800">
        <h3 className="text-xl font-black text-white">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#121824] border-b border-gray-800">
              <th className="text-left py-4 px-6 text-xs text-gray-500 font-bold uppercase tracking-wider w-1/3">Dimension</th>
              <th className="text-left py-4 px-6 text-xs font-black uppercase tracking-widest w-1/3" style={{ color: leftAccent }}>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: leftAccent, boxShadow: `0 0 10px ${leftAccent}` }} />
                  {leftHeader}
                </div>
              </th>
              <th className="text-left py-4 px-6 text-xs font-black uppercase tracking-widest w-1/3" style={{ color: rightAccent }}>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: rightAccent, boxShadow: `0 0 10px ${rightAccent}` }} />
                  {rightHeader}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/50">
            {data.rows.map((row: any, i: number) => {
              // Resilient mapping: find label/dimension and values
              const dimension = row.dimension || row.label || row.name || Object.values(row)[0];
              const leftVal = row.left_value || row.value_1 || row.institutional || Object.values(row)[1];
              const rightVal = row.right_value || row.value_2 || row.retail || Object.values(row)[2];

              if (!dimension || dimension === 'N/A') return null;
              return (
                <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                  <td className="py-4 px-6 text-gray-400 text-xs font-semibold">{dimension}</td>
                  <td className="py-4 px-6">
                    <div className="inline-block px-3 py-1.5 rounded-lg text-sm font-medium border" style={{ background: `${leftAccent}10`, color: '#e2e8f0', borderColor: `${leftAccent}30` }}>
                      {leftVal || '—'}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="inline-block px-3 py-1.5 rounded-lg text-sm font-medium border" style={{ background: `${rightAccent}10`, color: '#e2e8f0', borderColor: `${rightAccent}30` }}>
                      {rightVal || '—'}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {caption && <div className="p-4 bg-[#121824] text-xs text-gray-500 italic text-center border-t border-gray-800">{caption}</div>}
    </div>
  );
}

/** 7. COTAnalysisTable — Advanced institutional split bar */
function COTAnalysisTable({ personality, data }: { personality: Personality; data: any }) {
  const lp = data?.longs_pct ?? data?.managed_money_long_pct;
  const sp = data?.shorts_pct ?? data?.managed_money_short_pct;

  if (lp == null && sp == null) return null; // No breakdown available
  const longsP = Number(lp || 0);
  const shortsP = Number(sp || 0);

  const asset = data.asset && data.asset !== 'N/A' ? data.asset : data.pair && data.pair !== 'N/A' ? data.pair : null;
  const netPos = data.net_position && data.net_position !== 'N/A' ? data.net_position : 'NEUTRAL';
  const change = data.change_week && data.change_week !== 'N/A' ? data.change_week : null;
  const label = data.label && data.label !== 'N/A' ? data.label : 'Institutional Positioning';
  const interpretation = data.interpretation && data.interpretation !== 'N/A' ? data.interpretation : null;

  const isNetLong = netPos.toUpperCase().includes('LONG');
  const isNetShort = netPos.toUpperCase().includes('SHORT');
  const netColor = isNetLong ? DS.bullish : isNetShort ? DS.bearish : DS.neutral;

  return (
    <div className="my-10 p-6 sm:p-8 rounded-3xl bg-[#090D14] border shadow-2xl" style={{ borderColor: `${netColor}30` }}>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Landmark className="w-5 h-5" style={{ color: netColor }} />
            <h3 className="text-xl font-bold text-white">{label}</h3>
          </div>
          {asset && <div className="text-xs text-gray-500 font-semibold tracking-wider uppercase">Asset: {asset}</div>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <span
            className="px-4 py-1.5 rounded-full text-xs font-black tracking-widest uppercase border"
            style={{ background: `${netColor}15`, color: netColor, borderColor: `${netColor}40` }}
          >
            {netPos}
          </span>
          {change && <span className="text-[10px] text-gray-400 font-medium">Δ {change}</span>}
        </div>
      </div>

      <div className="relative mb-8">
        <div className="flex justify-between text-xs font-bold text-gray-400 mb-2 px-1 text-[10px] tracking-widest uppercase">
          <span style={{ color: DS.bullish }}>Longs ({longsP}%)</span>
          <span style={{ color: DS.bearish }}>Shorts ({shortsP}%)</span>
        </div>
        <div className="h-6 flex rounded-full overflow-hidden shadow-inner bg-[#121824] border border-gray-800 p-0.5">
          <div
            className="h-full rounded-l-full relative overflow-hidden transition-all duration-1000 ease-out"
            style={{ width: `${longsP}%`, background: DS.bullish }}
          >
            <div className="absolute inset-0 opacity-20" style={{ background: 'linear-gradient(90deg, transparent, white)' }} />
          </div>
          <div className="w-[2px] bg-white opacity-20 z-10" />
          <div
            className="h-full rounded-r-full relative overflow-hidden transition-all duration-1000 ease-out"
            style={{ width: `${shortsP}%`, background: DS.bearish }}
          >
            <div className="absolute inset-0 opacity-20" style={{ background: 'linear-gradient(270deg, transparent, white)' }} />
          </div>
        </div>
      </div>

      {interpretation && (
        <div
          className="p-5 rounded-2xl border text-sm text-gray-300 leading-relaxed font-medium"
          style={{ background: `${netColor}08`, borderColor: `${netColor}20` }}
        >
          <span className="font-bold block mb-1 text-xs uppercase tracking-widest" style={{ color: netColor }}>Intel Analysis</span>
          {interpretation}
        </div>
      )}
    </div>
  );
}

/** 8. SentimentHeatmap — Advanced Visual Heatmap */
function SentimentHeatmap({ personality, data }: { personality: Personality; data: any }) {
  if (data?.score == null || data.score === 'N/A' || data.score === '') return null;

  const tok = personalityTokens(personality);
  const score = Math.max(0, Math.min(100, Number(data.score)));
  const bias = data.bias && data.bias !== 'N/A' ? data.bias.toUpperCase() : 'NEUTRAL';
  const label = data.label && data.label !== 'N/A' ? data.label : 'Market Sentiment';
  const desc = data.description && data.description !== 'N/A' ? data.description : null;

  const biasColor = bias === 'BULLISH' ? DS.bullish : bias === 'BEARISH' ? DS.bearish : DS.neutral;

  const cells = Array.from({ length: 12 }, (_, i) => {
    const cellRangeMax = ((i + 1) / 12) * 100;
    const active = score >= cellRangeMax - (100 / 12);
    // Gradient logic: Early = Bearish, Mid = Warning/Neutral, Late = Bullish
    const hue = i < 4 ? DS.bearish : i < 8 ? DS.warning : DS.bullish;
    return { active, hue, isCurrent: active && score < cellRangeMax };
  });

  return (
    <div className="my-10 p-6 sm:p-8 rounded-3xl bg-[#0A0E17] border shadow-2xl relative overflow-hidden" style={{ borderColor: `${biasColor}30` }}>
      <div className="absolute top-0 right-0 w-64 h-64 blur-[80px] opacity-10 rounded-full" style={{ background: biasColor, transform: 'translate(30%, -30%)' }} />

      <div className="relative z-10 flex flex-wrap items-end justify-between gap-4 border-b pb-5 mb-8" style={{ borderColor: `${biasColor}20` }}>
        <div>
          <h3 className="text-2xl font-black text-white tracking-tight flex items-center gap-2">
            <BarChart3 className="w-6 h-6" style={{ color: tok.border }} />
            {label}
          </h3>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mb-1">Sentiment Score</div>
          <div className="text-4xl font-black font-mono leading-none tracking-tighter" style={{ color: biasColor, textShadow: `0 0 20px ${biasColor}40` }}>
            {score}
          </div>
        </div>
      </div>

      <div className="relative z-10 mb-8 pt-4">
        {/* Visual Map */}
        <div className="flex gap-1.5 h-10 mb-3">
          {cells.map((c, i) => (
            <div
              key={i}
              className={`flex-1 rounded-sm transition-all duration-500 relative ${c.isCurrent ? 'scale-y-125 z-10 shadow-lg' : ''}`}
              style={{
                background: c.active ? c.hue : `${c.hue}15`,
                opacity: c.active ? 1 : 0.4,
                boxShadow: c.active ? `0 0 10px ${c.hue}40` : 'none',
              }}
            >
              {c.isCurrent && (
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] font-black" style={{ color: c.hue }}>▼</div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] font-bold text-gray-500 uppercase tracking-widest px-1">
          <span>Extreme Fear</span>
          <span>Neutral</span>
          <span>Extreme Greed</span>
        </div>
      </div>

      <div className="relative z-10 flex flex-col md:flex-row items-center gap-4">
        <span
          className="px-5 py-2 rounded-xl text-sm font-black tracking-widest uppercase border shrink-0"
          style={{ background: `${biasColor}15`, color: biasColor, borderColor: `${biasColor}40` }}
        >
          {bias}
        </span>
        {desc && (
          <p className="text-sm text-gray-300 leading-relaxed font-medium md:border-l pl-0 md:pl-4" style={{ borderColor: `${biasColor}30` }}>
            {desc}
          </p>
        )}
      </div>
    </div>
  );
}

/** 9. LiquidityMap — Advanced Order Book Heatmap */
function LiquidityMap({ personality, data }: { personality: Personality; data: any }) {
  if (!data?.levels && !data?.zones) return null;
  const levels = data.levels ?? data.zones;
  if (!Array.isArray(levels) || levels.length === 0) return null;

  const tok = personalityTokens(personality);
  const asset = data.asset && data.asset !== 'N/A' ? data.asset : null;
  const currentPrice = Number(data.current_price || 0);
  const caption = data.caption && data.caption !== 'N/A' ? data.caption : null;

  const getStrengthMeta = (s: string) => {
    switch (s?.toUpperCase()) {
      case 'EXTREME': return { op: 1, label: 'EXTREME VOL' };
      case 'HIGH': return { op: 0.8, label: 'HIGH VOL' };
      case 'MEDIUM': return { op: 0.5, label: 'MED VOL' };
      case 'LOW': return { op: 0.3, label: 'LOW VOL' };
      default: return { op: 0.6, label: 'VOL' };
    }
  };

  const sorted = [...levels].sort((a, b) => {
    const pA = Number(a.price ?? a.price_high ?? 0);
    const pB = Number(b.price ?? b.price_high ?? 0);
    return pB - pA;
  });

  return (
    <div className="my-10 p-6 sm:p-8 rounded-3xl bg-[#10141C] border shadow-2xl relative overflow-hidden" style={{ borderColor: `${tok.border}30` }}>
      <div className="relative z-10 flex flex-wrap items-center justify-between gap-4 mb-6 border-b pb-4" style={{ borderColor: `${tok.border}20` }}>
        <div className="flex bg-[#0A0E17] border rounded-2xl p-2 items-center gap-3 pr-6" style={{ borderColor: `${tok.border}30` }}>
          <div className="w-12 h-12 flex items-center justify-center rounded-xl" style={{ background: `${tok.border}20` }}>
            <Activity className="w-6 h-6" style={{ color: tok.border }} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white tracking-tight">Institutional Liquidity Map</h3>
            {asset && <div className="text-xs text-gray-500 font-semibold uppercase tracking-widest">{asset}</div>}
          </div>
        </div>
        {currentPrice > 0 && (
          <div className="text-right">
            <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">Current Price</div>
            <div className="text-2xl font-black font-mono tracking-tight" style={{ color: DS.neutral, textShadow: `0 0 15px ${DS.neutral}40` }}>
              {currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
          </div>
        )}
      </div>

      <div className="relative z-10 space-y-2.5">
        {sorted.map((lvl: any, i: number) => {
          const price = Number(lvl.price ?? lvl.price_high ?? 0);
          if (price === 0) return null;

          const side = (lvl.type ?? lvl.side ?? 'BUY').toUpperCase();
          const isBuy = side.includes('BUY') || side.includes('SUPPORT');
          const col = isBuy ? DS.bullish : DS.bearish;
          const { op, label } = getStrengthMeta(lvl.strength);

          let isCurrentZone = false;
          let diffStr = '';
          if (currentPrice > 0) {
            const diff = price - currentPrice;
            const diffPct = Math.abs(diff) / currentPrice;
            isCurrentZone = diffPct < 0.002;
            diffStr = diff > 0 ? `+${diff.toFixed(2)}` : diff.toFixed(2);
          }

          return (
            <div key={i} className="relative group">
              {isCurrentZone && (
                <div className="absolute -inset-1 rounded-xl blur-md opacity-50 z-0 animate-pulse" style={{ background: DS.neutral }} />
              )}
              <div
                className="relative z-10 flex items-center gap-3 py-3 px-4 rounded-xl transition-all duration-300 group-hover:translate-x-1"
                style={{
                  background: `linear-gradient(90deg, ${col}${Math.round((op * 30)).toString(16).padStart(2, '0')} 0%, transparent 100%)`,
                  border: isCurrentZone ? `1px solid ${DS.neutral}80` : `1px solid ${col}20`,
                  borderLeft: `4px solid ${col}`
                }}
              >
                <span className="w-16 text-center text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded" style={{ background: `${col}15`, color: col }}>
                  {side}
                </span>
                <span className="font-mono text-lg font-bold flex-1" style={{ color: isCurrentZone ? DS.neutral : 'white' }}>
                  {price.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>

                <div className="text-right flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                  {(lvl.label || lvl.type) && lvl.label !== 'N/A' && (
                    <span className="text-xs text-gray-300 font-medium">{lvl.label ?? lvl.type}</span>
                  )}
                  <span className="hidden sm:inline-block text-[10px] font-bold text-gray-500 uppercase tracking-widest">{label}</span>
                  {currentPrice > 0 && (
                    <span className="text-[10px] font-mono font-medium" style={{ color: diffStr.startsWith('+') ? DS.neutral : DS.bearish }}>
                      {diffStr}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {caption && <div className="relative z-10 mt-6 pt-4 border-t border-gray-800 text-center text-xs text-gray-500 italic">{caption}</div>}
    </div>
  );
}

/** 10. RiskCalculator — Advanced Execution Dashboard */
function RiskCalculator({ personality, data }: { personality: Personality; data: any }) {
  const fields = [
    { label: 'Asset', val: data?.asset },
    { label: 'Acct Size', val: data?.account_size ? `$${Number(data.account_size).toLocaleString()}` : null },
    { label: 'Risk %', val: data?.risk_pct ?? data?.risk_percent ? `${data?.risk_pct ?? data?.risk_percent}%` : null },
    { label: 'Entry', val: data?.entry ?? data?.entry_price },
    { label: 'Stop Loss', val: data?.stop_loss },
    { label: 'Take Profit', val: data?.take_profit ?? data?.take_profit_1 },
    { label: 'Pip Value', val: data?.pip_value ? `$${data.pip_value}` : null },
    { label: 'Position Size', val: data?.position_size ? `${data.position_size} Lots` : null },
    { label: 'Risk Amount', val: data?.risk_amount ? `$${data.risk_amount}` : null, highlight: DS.warning },
    { label: 'Calculated R:R', val: data?.risk_reward, highlight: DS.accent },
  ].filter(f => f.val != null && f.val !== 'N/A' && f.val !== '');

  if (fields.length === 0) return null;

  const tok = personalityTokens(personality);

  return (
    <div className="my-10 p-1 rounded-3xl" style={{ background: `linear-gradient(180deg, ${tok.border}80 0%, ${tok.border}00 100%)` }}>
      <div className="bg-[#090D14] rounded-[1.4rem] p-6 sm:p-8">
        <h3 className="text-lg font-black text-white uppercase tracking-widest mb-6 border-b pb-4" style={{ borderColor: `${tok.border}20` }}>
          Risk & Execution Parameters
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {fields.map((f, i) => (
            <div
              key={i}
              className="p-4 rounded-2xl flex flex-col justify-between"
              style={{ background: '#121824', border: f.highlight ? `1px solid ${f.highlight}40` : '1px solid #1f2937' }}
            >
              <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-2">{f.label}</div>
              <div
                className={`font-mono text-base font-bold ${f.highlight ? 'text-white' : 'text-gray-200'}`}
                style={f.highlight ? { color: f.highlight, textShadow: `0 0 10px ${f.highlight}30` } : {}}
              >
                {f.val}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center gap-3 p-3 rounded-lg bg-yellow-950/20 border border-yellow-900/30">
          <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" />
          <p className="text-[10px] text-yellow-500/80 font-medium uppercase tracking-widest">
            Capital at risk. Verify all volume constraints before active execution.
          </p>
        </div>
      </div>
    </div>
  );
}

/** 11. KeyLevelsBox — Institutional Supply/Demand Levels */
function KeyLevelsBox(props: any) {
  const data = props.data || props;
  const levels = data.levels || data.key_levels || [];
  if (!Array.isArray(levels) || levels.length === 0) return null;

  return (
    <div className="my-10 p-8 rounded-3xl bg-[#0A0E17]/80 backdrop-blur-xl border border-white/5 shadow-2xl relative overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4A537]/10 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h3 className="text-xl font-black text-white uppercase tracking-tight font-['Orbitron']">Institutional Key Levels</h3>
          <p className="text-[10px] text-gray-500 font-bold uppercase tracking-[4px] mt-1 italic">V.II Intelligence Feed</p>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3].map(i => <div key={i} className="w-1 h-1 rounded-full bg-[#D4A537] animate-pulse" />)}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {levels.map((lvl: any, idx: number) => {
          const isExtreme = lvl.strength === 'Extreme' || lvl.strength === 'Critical';
          const isSupply = (lvl.type || '').toLowerCase().includes('supply') || (lvl.side || '').toLowerCase().includes('sell');
          const col = isSupply ? '#EF4444' : '#10B981';
          return (
            <div key={idx} className="group relative bg-white/[0.03] p-6 rounded-2xl border border-white/[0.05] hover:border-[#D4A537]/30 transition-all duration-500 hover:-translate-y-1">
              <div className="flex justify-between items-start mb-4">
                <span className="text-[9px] font-black uppercase tracking-[2px] px-2 py-0.5 rounded bg-white/5 text-gray-400 border border-white/10 group-hover:border-[#D4A537]/20 transition-colors">
                  {lvl.type || (isSupply ? 'Supply' : 'Demand')}
                </span>
                {isExtreme && (
                  <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#D4A537]/10 border border-[#D4A537]/20 animate-pulse">
                    <ShieldAlert className="w-3 h-3 text-[#D4A537]" />
                    <span className="text-[8px] font-black text-[#D4A537] uppercase tracking-wider">Critical</span>
                  </div>
                )}
              </div>
              <div className="text-3xl font-black tabular-nums tracking-tighter mb-2 font-['Orbitron'] group-hover:text-[#D4A537] transition-colors" style={{ color: isExtreme ? '#D4A537' : 'white' }}>
                {lvl.price}
              </div>
              <div className="space-y-3">
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-relaxed">
                  {lvl.label || 'Technical Level'}
                </div>
                {lvl.note && (
                  <p className="text-[11px] text-gray-500 italic leading-snug border-l border-white/10 pl-3">
                    {lvl.note}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** 12. ScenarioMatrix — Tactical Intelligence Scenarios */
function ScenarioMatrix({ personality, data }: { personality: Personality; data: any }) {
  const scenarios = data?.scenarios ?? [];
  if (!Array.isArray(scenarios) || scenarios.length === 0) return null;
  const title = data.title || "Tactical Intelligence Scenario Matrix";

  const getBiasStyles = (bias: string) => {
    const b = (bias || '').toUpperCase();
    if (b.includes('BULL')) {
      return {
        text: 'text-[#10B981]',
        bg: 'bg-[#10B981]/10',
        glow: 'shadow-[0_0_50px_-10px_rgba(16,185,129,0.3)]',
        border: 'border-[#10B981]/30',
        accent: '#10B981',
        icon: <TrendingUpIcon className="w-5 h-5 text-[#10B981]" />
      };
    } else if (b.includes('BEAR')) {
      return {
        text: 'text-[#ef4444]',
        bg: 'bg-[#ef4444]/10',
        glow: 'shadow-[0_0_50px_-10px_rgba(239,68,68,0.3)]',
        border: 'border-[#ef4444]/30',
        accent: '#ef4444',
        icon: <ShieldAlert className="w-5 h-5 text-[#ef4444]" />
      };
    } else {
      return {
        text: 'text-[#D4A537]',
        bg: 'bg-[#D4A537]/10',
        glow: 'shadow-[0_0_50px_-10px_rgba(212,165,55,0.3)]',
        border: 'border-[#D4A537]/30',
        accent: '#D4A537',
        icon: <Activity className="w-5 h-5 text-[#D4A537]" />
      };
    }
  };

  return (
    <div className="w-full bg-[#0A0E17] py-12 px-6 font-['Inter'] antialiased rounded-3xl overflow-hidden border border-white/5 my-10">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700&family=Inter:wght@400;600;800&display=swap');
        .scenario-glass-card {
          background: rgba(26, 31, 46, 0.4);
          backdrop-filter: blur(24px);
        }
      `}</style>

      <div className="max-w-7xl mx-auto">
        <div className="mb-12 border-l-4 border-[#D4A537] pl-8">
          <p className="text-[#D4A537] text-[10px] font-bold tracking-[6px] uppercase mb-3 font-['Orbitron']">
            Protocol V.4.2 — Alpha Intelligence
          </p>
          <h2 className="text-white text-3xl md:text-5xl font-black uppercase tracking-tight leading-none mb-6">
            {title}
          </h2>
          <p className="text-gray-500 text-base max-w-3xl leading-relaxed">
            Institutional-grade predictive modeling. Scenarios are calculated using macro variables and real-time structural bias analysis.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {scenarios.map((scenario: any, index: number) => {
            const styles = getBiasStyles(scenario.bias);
            const probNum = parseInt(scenario.probability) || 0;

            return (
              <div
                key={index}
                className={`
                  relative scenario-glass-card p-10 rounded-[24px] border transition-all duration-700
                  hover:-translate-y-3 group ${styles.glow} ${styles.border}
                `}
              >
                <div className="flex items-center justify-between mb-10">
                  <div className={`
                    px-5 py-2 rounded-lg border font-['Orbitron'] text-[11px] font-bold tracking-[3px] uppercase
                    ${styles.bg} ${styles.text} ${styles.border}
                  `}>
                    BIAS: {scenario.bias}
                  </div>
                  <div className={`p-3 rounded-xl ${styles.bg}`}>
                    {styles.icon}
                  </div>
                </div>

                <div className="space-y-8">
                  <div className="relative">
                    <p className="text-gray-600 text-[9px] font-bold uppercase tracking-[4px] mb-3">Primary Trigger</p>
                    <p className="text-white text-xl font-bold tracking-tight leading-snug">
                      {scenario.trigger}
                    </p>
                    <div className="absolute -left-6 top-6 w-1 h-8 bg-gray-800 rounded-full" />
                  </div>

                  <div>
                    <p className="text-gray-600 text-[9px] font-bold uppercase tracking-[4px] mb-3">Institutional Flow Reaction</p>
                    <p className={`${styles.text} text-lg font-black tracking-wide leading-relaxed font-['Orbitron']`}>
                      {scenario.gold_reaction || scenario.reaction}
                    </p>
                  </div>

                  <div className="pt-8 flex flex-col items-center">
                    <p className="text-gray-600 text-[9px] font-bold uppercase tracking-[4px] mb-6">Probability Score</p>
                    <div className="relative w-32 h-32 flex items-center justify-center">
                      <svg className="w-full h-full -rotate-90">
                        <circle cx="64" cy="64" r="56" fill="transparent" stroke="rgba(255,255,255,0.03)" strokeWidth="10" />
                        <circle
                          cx="64" cy="64" r="56" fill="transparent" stroke={styles.accent} strokeWidth="10"
                          strokeDasharray="351.8" strokeDashoffset={351.8 - (351.8 * probNum) / 100}
                          strokeLinecap="round" className="transition-all duration-[1500ms] ease-out drop-shadow-[0_0_12px_rgba(255,255,255,0.3)]"
                        />
                      </svg>
                      <div className="absolute flex flex-col items-center">
                        <span className={`font-['Orbitron'] text-3xl font-black ${styles.text}`}>
                          {scenario.probability}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** 13. PriceTable — Sovereign Analytic Pricing */
function PriceTable(props: any) {
  const data = props.data || props;
  const prices = data.prices || [];
  if (!Array.isArray(prices) || prices.length === 0) return null;
  const title = data.title || "Global Price Board";
  const subtitle = data.subtitle || "Live Institutional Feed // Latency 4ms";

  const getSentimentStyles = (sentiment: string) => {
    const s = (sentiment || '').toUpperCase();
    if (s.includes('BULL') || s.includes('STRONG')) {
      return { text: 'text-[#10B981]', bg: 'bg-[#10B981]/10', icon: <TrendingUpIcon className="w-3 h-3" />, glow: 'shadow-[0_0_15px_rgba(16,185,129,0.2)]' };
    } else if (s.includes('BEAR')) {
      return { text: 'text-[#ef4444]', bg: 'bg-[#ef4444]/10', icon: <TrendingDownIcon className="w-3 h-3" />, glow: 'shadow-[0_0_15px_rgba(239,68,68,0.2)]' };
    } else {
      return { text: 'text-[#D4A537]', bg: 'bg-[#D4A537]/10', icon: <MinusIcon className="w-3 h-3" />, glow: 'shadow-[0_0_15px_rgba(212,165,55,0.2)]' };
    }
  };

  return (
    <div className="w-full bg-[#0A0E17] font-['Inter'] antialiased border border-white/5 rounded-3xl overflow-hidden my-10">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700&family=Cinzel:wght@700;900&display=swap');
        .font-cinzel { font-family: 'Cinzel', serif; }
        .glass-row {
          background: rgba(255, 255, 255, 0.02);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .glass-row:hover {
          background: rgba(255, 255, 255, 0.05);
          box-shadow: inset 0 0 20px rgba(212, 165, 55, 0.05);
          transform: translateY(-1px);
        }
      `}</style>

      <div className="max-w-7xl mx-auto py-8">
        <div className="mb-8 px-6 border-l-4 border-[#D4A537]">
          <h2 className="text-white text-3xl font-black uppercase tracking-tight font-cinzel leading-none">
            {title}
          </h2>
          <p className="text-gray-600 text-[10px] font-bold uppercase tracking-[3px] mt-2 font-['Orbitron']">
            {subtitle}
          </p>
        </div>

        <div className="overflow-x-auto w-full">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-6 py-4 text-gray-600 text-[9px] font-bold uppercase tracking-[2px] font-['Orbitron']">Pair</th>
                <th className="px-6 py-4 text-gray-600 text-[9px] font-bold uppercase tracking-[2px] font-['Orbitron'] text-right">Price</th>
                <th className="px-6 py-4 text-gray-600 text-[9px] font-bold uppercase tracking-[2px] font-['Orbitron'] text-right">Change</th>
                <th className="px-6 py-4 text-gray-600 text-[9px] font-bold uppercase tracking-[2px] font-['Orbitron'] text-right">Sentiment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {prices.map((item: any, index: number) => {
                const styles = getSentimentStyles(item.sentiment);
                const isPositive = typeof item.change === 'number' ? item.change > 0 : String(item.change).startsWith('+');

                return (
                  <tr key={index} className="glass-row group">
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-white text-sm font-extrabold tracking-tight group-hover:text-[#D4A537] transition-colors">{item.pair}</span>
                        <span className="text-[9px] text-gray-600 font-bold uppercase tracking-wider font-['Orbitron']">{item.pair.split('/')[0]} Core</span>
                      </div>
                    </td>
                    <td className="px-6 py-5 text-right whitespace-nowrap">
                      <span className="text-white text-base font-bold font-['Orbitron'] tabular-nums tracking-tighter">{item.price}</span>
                    </td>
                    <td className="px-6 py-5 text-right whitespace-nowrap">
                      <span className={`text-xs font-bold font-['Orbitron'] ${isPositive ? 'text-[#10B981]' : 'text-[#ef4444]'}`}>
                        {item.change}%
                      </span>
                    </td>
                    <td className="px-6 py-5 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/5 bg-white/[0.03]">
                         <div className={`p-0.5 rounded-full ${styles.bg} ${styles.text} ${styles.glow}`}>{styles.icon}</div>
                         <span className={`text-[9px] font-black uppercase tracking-widest font-['Orbitron'] ${styles.text}`}>{item.sentiment}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT MAPPER (The Brain)
// Maps component_id → React component. Unknown IDs render a safe fallback.
// ═══════════════════════════════════════════════════════════════════════════

const COMPONENT_REGISTRY: Record<string, React.FC<{ personality: Personality; data: any }>> = {
  // Primary names
  StatPulse,
  CalloutBox,
  MermaidChart: MermaidChartStub,
  PriceActionTimeline,
  TradingSetupCard,
  ComparisonMatrix,
  COTAnalysisTable,
  SentimentHeatmap,
  LiquidityMap,
  RiskCalculator,

  PriceTable,
  ScenarioMatrix,
  KeyLevelsBox,
  MidArticleCTA,

  // Database aliases (snake_case)
  stat_pulse: StatPulse,
  stat_highlight: StatPulse,
  callout_box: CalloutBox,
  mermaid_chart: MermaidChartStub,
  price_table: PriceTable,
  scenario_matrix: ScenarioMatrix,
  key_levels: KeyLevelsBox,
  mid_article_cta: MidArticleCTA,
  midarticlecta: MidArticleCTA,
  trading_setup: TradingSetupCard,
  comparison_matrix: ComparisonMatrix,
  cot_table: COTAnalysisTable,
  sentiment_heatmap: SentimentHeatmap,
  liquidity_map: LiquidityMap,
  risk_calc: RiskCalculator,
  quote_block: CalloutBox,

  // Legacy aliases
  SentimentMeter: SentimentHeatmap,
  COTBar: COTAnalysisTable,
  MermaidFlowchart: MermaidChartStub,
  LiquidityTable: LiquidityMap,
  OrderFlowMap: LiquidityMap,
};

function ArticleComponentMapper({ entry }: { entry: ManifestEntry }) {
  const { component_id, personality, data, type, id } = entry;
  // Use component_id, id, or fallback to type (from DB array structure)
  const lookupId = component_id || id || type;
  const Comp = COMPONENT_REGISTRY[lookupId as string];

  if (!Comp) {
    // Unknown component_id — fail gracefully
    if (process.env.NODE_ENV !== 'production') {
      return (
        <div className="my-6 p-4 bg-gray-900/50 border border-dashed border-gray-700 rounded-xl text-xs text-gray-500 font-mono">
          <div className="text-yellow-500 font-bold mb-1">Unknown component_id: "{component_id}"</div>
          <div className="text-gray-600">Add it to COMPONENT_REGISTRY in ArticleRenderer.tsx</div>
          <pre className="mt-2 text-gray-700 whitespace-pre-wrap text-[10px]">
            {JSON.stringify({ component_id, personality, data }, null, 2)}
          </pre>
        </div>
      );
    }
    return null; // Silently skip in production
  }

  return (
    <ComponentErrorBoundary componentId={component_id}>
      <Comp personality={personality} data={data ?? {}} />
    </ComponentErrorBoundary>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MARKDOWN HELPERS (retained from original ArticleRenderer.jsx)
// ═══════════════════════════════════════════════════════════════════════════

const FOREXFUNDAI_HOST = 'forexfundai.com';

function isInternal(href = '') {
  return href.startsWith('/') || href.includes(FOREXFUNDAI_HOST);
}

function headingId(text: any): string {
  return String(text)
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

function calloutVariant(text: any): string {
  const t = String(text).trim();
  if (t.startsWith('💡')) return 'insight';
  if (t.startsWith('⚠️') || t.startsWith('⚠')) return 'warning';
  if (t.startsWith('📊')) return 'data';
  if (t.startsWith('🔑')) return 'framework';
  if (t.startsWith('📈')) return 'history';
  return 'default';
}

const mdComponents: any = {
  h1: ({ children }: any) => {
    const id = headingId(children);
    return <h1 id={id} className="text-3xl sm:text-4xl font-extrabold mt-12 mb-6 tracking-tight" style={{ color: '#D4A537', scrollMarginTop: '6rem' }}>{children}</h1>;
  },
  h2: ({ children }: any) => {
    const id = headingId(children);
    return <h2 id={id} className="text-2xl sm:text-3xl font-bold mt-10 mb-5 pb-2 border-b border-gray-800" style={{ color: '#D4A537', scrollMarginTop: '6rem' }}>{children}</h2>;
  },
  h3: ({ children }: any) => {
    const id = headingId(children);
    return <h3 id={id} className="text-xl font-bold mt-8 mb-4 border-l-4 border-[#D4A537] pl-4" style={{ color: '#D4A537', scrollMarginTop: '6rem' }}>{children}</h3>;
  },
  h4: ({ children }: any) => <h4 className="text-lg font-semibold text-gray-300 mt-6 mb-3">{children}</h4>,

  p: ({ children }: any) => <p className="text-gray-300 leading-relaxed text-[1.05rem] mb-6">{children}</p>,

  strong: ({ children }: any) => <strong className="font-bold text-white bg-gray-800/30 px-1 py-0.5 rounded">{children}</strong>,
  em: ({ children }: any) => <em className="italic text-gray-400">{children}</em>,

  table: ({ children }: any) => (
    <div className="overflow-x-auto my-8 border border-gray-800 rounded-xl shadow-lg bg-gray-900/40">
      <table className="w-full text-left text-sm whitespace-nowrap">{children}</table>
    </div>
  ),
  thead: ({ children }: any) => <thead className="bg-gray-800/80 text-gray-200 uppercase text-xs tracking-wider border-b border-gray-700">{children}</thead>,
  th: ({ children }: any) => <th className="px-6 py-4 font-semibold">{children}</th>,
  tbody: ({ children }: any) => <tbody className="divide-y divide-gray-800/60">{children}</tbody>,
  tr: ({ children }: any) => <tr className="hover:bg-gray-800/30 transition-colors">{children}</tr>,
  td: ({ children }: any) => {
    const text = String(children ?? '');
    const isBullish = /bullish/i.test(text);
    const isBearish = /bearish/i.test(text);
    const isNeutral = /neutral/i.test(text);
    let cls = 'px-6 py-4 text-gray-300';
    if (isBullish) cls += ' text-emerald-400 font-medium bg-emerald-500/5';
    if (isBearish) cls += ' text-rose-400 font-medium bg-rose-500/5';
    if (isNeutral) cls += ' text-yellow-400 font-medium bg-yellow-500/5';
    return <td className={cls}>{children}</td>;
  },

  blockquote: ({ children }: any) => {
    const raw = children?.props?.children?.[0]?.props?.children?.[0] ?? '';
    const variant = calloutVariant(raw);
    let borderCls = 'border-l-4 border-gray-600';
    let bgCls = 'bg-gray-800/30';
    let textCls = 'text-gray-300';
    if (variant === 'insight') { borderCls = `border-l-4`; bgCls = `bg-[${DS.accent}]/5`; textCls = 'text-gray-200'; }
    if (variant === 'warning') { borderCls = 'border-l-4 border-rose-500'; bgCls = 'bg-rose-500/5'; }
    if (variant === 'data') { borderCls = 'border-l-4 border-blue-500'; bgCls = 'bg-blue-500/5'; }
    return (
      <div className={`my-8 p-6 rounded-r-xl ${borderCls} ${bgCls} shadow-md`} style={variant === 'insight' ? { borderLeftColor: DS.accent } : {}}>
        <div className={`italic ${textCls} text-lg`}>{children}</div>
      </div>
    );
  },

  code: ({ inline, className, children }: any) => {
    const lang = /language-(\w+)/.exec(className ?? '')?.[1];
    if (lang === 'mermaid') {
      return (
        <Suspense fallback={<div className="w-full h-48 my-8 flex items-center justify-center bg-gray-900 rounded-xl border border-gray-800 animate-pulse"><span className="text-gray-500">Loading chart…</span></div>}>
          <div className="my-8 border border-gray-800 rounded-xl overflow-hidden bg-gray-900 shadow-xl p-4">
            <MermaidChart chart={String(children)} />
          </div>
        </Suspense>
      );
    }
    if (inline) return <code className="px-1.5 py-0.5 rounded-md bg-gray-800 text-sm font-mono border border-gray-700" style={{ color: DS.accent }}>{children}</code>;
    return (
      <pre className="my-8 p-5 rounded-xl bg-gray-900 text-gray-300 text-sm font-mono overflow-x-auto border border-gray-800 shadow-inner">
        <code className={className}>{children}</code>
      </pre>
    );
  },

  pre: ({ children }: any) => <>{children}</>,

  a: ({ href, children }: any) => {
    const baseCls = "font-medium hover:text-yellow-300 underline underline-offset-4 decoration-gray-700 hover:decoration-current transition-all";
    const style = { color: DS.accent };
    if (isInternal(href ?? '')) {
      const to = (href as string).replace(/^https?:\/\/forexfundai\.com/, '');
      return <Link to={to || '/'} className={baseCls} style={style}>{children}</Link>;
    }
    return <a href={href} target="_blank" rel="noopener noreferrer" className={baseCls} style={style}>{children}</a>;
  },

  ul: ({ children }: any) => <ul className="list-none space-y-3 my-6 text-gray-300">{children}</ul>,
  ol: ({ children }: any) => <ol className="list-decimal list-outside ml-6 space-y-3 my-6 text-gray-300 text-lg">{children}</ol>,
  li: ({ children }: any) => (
    <li className="relative pl-6 before:content-['▸'] before:absolute before:left-0 before:text-sm before:top-[4px]" style={{ '--tw-before-color': DS.accent } as any}>
      <span className="absolute left-0 top-[4px] text-sm" style={{ color: DS.accent }}>▸</span>
      {children}
    </li>
  ),
  hr: () => <hr className="my-12 border-t border-gray-800/80 mx-auto w-2/3" />,

  // Institutional Custom Tags Support
  midarticlecta: (props: any) => <MidArticleCTA personality="pro" data={props} />,
  key_levels: (props: any) => <KeyLevelsBox personality="pro" data={props} />,
  scenario_matrix: (props: any) => <ScenarioMatrix personality="pro" data={props} />,
  price_table: (props: any) => <PriceTable personality="pro" data={props} />,
  stat_pulse: (props: any) => <StatPulse personality="pro" data={props} />,
  callout_box: (props: any) => <CalloutBox personality="pro" data={props} />,
  trading_setup: (props: any) => <TradingSetupCard personality="pro" data={props} />,
  
  // Backward compatibility & Aliases
  stat_highlight: (props: any) => <StatPulse personality="pro" data={props} />,
  quote_block: ({ personality, data }: any) => (
    <div className="my-12 relative p-8 md:p-12 rounded-[2rem] bg-gray-900/40 border-l-8 border-[#D4A537] italic shadow-2xl overflow-hidden">
      <div className="absolute top-0 right-0 p-4 opacity-5">
        <Landmark className="w-24 h-24 text-white" />
      </div>
       <p className="text-xl md:text-2xl text-white font-medium mb-6 leading-relaxed relative z-10">"{data.quote}"</p>
       {data.attribution && (
         <div className="flex items-center gap-3">
           <div className="w-8 h-[1px] bg-[#D4A537]" />
           <span className="text-sm font-black uppercase tracking-[3px] text-[#D4A537]">{data.attribution}</span>
         </div>
       )}
    </div>
  ),
  mermaid_chart: MermaidChartStub,
};

// ═══════════════════════════════════════════════════════════════════════════
// PLACEHOLDER PARSER
// Splits content string by [RENDER_COMP_X] placeholders and returns an
// ordered array of { type: 'text' | 'component', value: string }.
// ═══════════════════════════════════════════════════════════════════════════

const PLACEHOLDER_REGEX = /\[RENDER_COMP_([a-zA-Z0-9_-]+)\]/g;

type ContentSegment =
  | { type: 'text'; value: string }
  | { type: 'component'; placeholder: string };

function parseContentSegments(content: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  PLACEHOLDER_REGEX.lastIndex = 0; // Reset stateful regex
  while ((match = PLACEHOLDER_REGEX.exec(content)) !== null) {
    const textBefore = content.slice(lastIndex, match.index);
    if (textBefore) {
      segments.push({ type: 'text', value: textBefore });
    }
    // Normalize placeholder ID for lookup (e.g. COMP_0 for RENDER_COMP_COMP_0 is inconsistent; extract just the index if needed)
    // We normalize to the key format used in normalizedManifest (COMP_00X)
    const rawId = match[1]; 
    let normId = rawId;
    if (rawId.startsWith('COMP_')) {
      const parts = rawId.split('_');
      const last = parts[parts.length - 1]; // e.g. "004"
      if (!isNaN(parseInt(last))) {
        normId = `COMP_${last.padStart(3, '0')}`;
      }
    } else if (!isNaN(parseInt(rawId))) {
       normId = `COMP_${rawId.padStart(3, '0')}`;
    }

    segments.push({ type: 'component', placeholder: normId });
    lastIndex = match.index + match[0].length;
  }

  const textAfter = content.slice(lastIndex);
  if (textAfter) {
    segments.push({ type: 'text', value: textAfter });
  }

  return segments;
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN EXPORT — ArticleRenderer
// ═══════════════════════════════════════════════════════════════════════════

export default function ArticleRenderer({ content, manifest, className = '' }: ArticleRendererProps) {
  if (!content) return null;

  // ── Normalize Manifest ──
  // If manifest is an array (from db ui_components), convert to indexed map.
  // We use sequential mapping (order of placeholders in text = order of array items)
  // to handle cases where the AI uses paragraph indices (e.g. COMP_004) as IDs.
  const normalizedManifest: ArticleManifest = useMemo(() => {
    if (!manifest) return {};
    if (Array.isArray(manifest)) {
      const map: ArticleManifest = {};

      // 1. Scan content for all unique placeholders in order of appearance
      const matches = Array.from(content.matchAll(PLACEHOLDER_REGEX));
      const foundPlaceholders = Array.from(new Set(matches.map(m => `RENDER_COMP_${m[1]}`)));

      // 2. Map by ID (Prioritize specific IDs like 'macro-bias-pulse')
      foundPlaceholders.forEach(ph => {
        const slug = ph.replace('RENDER_COMP_', '');
        const found = manifest.find((m: any) => m.id === slug || m.component_id === slug);
        if (found) {
          map[ph] = found;
        }
      });

      // 2. Map by index (Legacy/Sequential)
      // If placeholder is [RENDER_COMP_4], map to manifest[4]
      foundPlaceholders.forEach(ph => {
        if (!map[ph]) {
          const indexMatch = ph.match(/(\d+)/);
          if (indexMatch) {
            const idx = parseInt(indexMatch[0], 10);
            if (manifest[idx]) {
              map[ph] = manifest[idx];
            }
          }
        }
      });

      // 3. Fallback: Sequential Backfill
      foundPlaceholders.forEach((ph, i) => {
        if (!map[ph] && manifest[i]) {
          map[ph] = manifest[i];
        }
      });

      return map;
    }
    return manifest as ArticleManifest;
  }, [manifest, content]);

  const hasManifest = Object.keys(normalizedManifest).length > 0;

  // ── MODE A: No manifest — pure Markdown render (legacy compatibility) ──
  if (!hasManifest) {
    return (
      <div className={`prose prose-invert prose-lg max-w-none prose-headings:text-white prose-a:text-[${DS.accent}] ${className}`}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            ...mdComponents,
            // backward compat: inline placeholder tags from old pipeline
            cotbar: () => <div className="p-4 bg-gray-900 border border-gray-800 rounded-xl text-center my-6" style={{ color: DS.accent }}>COT Bar ⟶ update pipeline to v2 manifest</div>,
            liquiditytable: () => <div className="p-4 bg-gray-900 border border-gray-800 rounded-xl text-blue-400 text-center my-6">Liquidity Table ⟶ update pipeline to v2 manifest</div>,
            sentimentmeter: () => <div className="p-4 bg-gray-900 border border-gray-800 rounded-xl text-emerald-400 text-center my-6">Sentiment Meter ⟶ update pipeline to v2 manifest</div>,
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  }

  // ── MODE B: Manifest mode — parse [RENDER_COMP_X] and inject components ──
  const segments = parseContentSegments(content);

  return (
    <div className={`max-w-none ${className}`}>
      {segments.map((seg, i) => {
        if (seg.type === 'text') {
          // Render text chunk as Markdown
          return (
            <div
              key={i}
              className={`prose prose-invert prose-lg max-w-none prose-headings:text-white`}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={mdComponents}
              >
                {seg.value}
              </ReactMarkdown>
            </div>
          );
        }

        // Render component from manifest
        const lookupId = seg.placeholder; // Normalized to COMP_00X
        const entry = normalizedManifest[lookupId];

        if (!entry) {
          // Fallback: try raw index lookup
          const idx = parseInt(lookupId.replace('COMP_', ''));
          const fallbackEntry = Array.isArray(manifest) ? manifest[idx] : null;
          
          if (!fallbackEntry) {
            if (process.env.NODE_ENV !== 'production') {
              return (
                <div key={i} className="my-10 p-4 rounded-2xl bg-gray-900/50 border border-gray-800 text-center">
                  <div className="text-orange-400 font-mono text-xs uppercase tracking-widest mb-1">Missing Execution Unit</div>
                  <div className="text-gray-500 text-[10px]">{lookupId} reference failed in manifest.</div>
                </div>
              );
            }
            return null;
          }
          return <div key={i} className="my-12"><ArticleComponentMapper entry={fallbackEntry} /></div>;
        }

        return (
          <div key={i} className="my-12">
            <ArticleComponentMapper entry={entry} />
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// NAMED EXPORTS — for direct use in tests or other renderers
// ═══════════════════════════════════════════════════════════════════════════
export {
  ArticleComponentMapper,
  ComponentErrorBoundary,
  parseContentSegments,
  COMPONENT_REGISTRY,
  StatPulse,
  CalloutBox,
  MermaidChartStub,
  PriceActionTimeline,
  TradingSetupCard,
  ComparisonMatrix,
  COTAnalysisTable,
  SentimentHeatmap,
  LiquidityMap,
  RiskCalculator,
  KeyLevelsBox,
  ScenarioMatrix,
  PriceTable,
  MidArticleCTA,
};
